import type { FC, ReactNode } from 'react'

import {
    useEffect,
    useMemo,
    useState
} from 'react'

import { cn } from '@/lib/utils'
import { useInView } from '@/hooks/use-in-view'

interface TypedHeadingProps {
    text: string
    id?: string
    className?: string
    speed?: number
}

/**
 * A heading that types itself out with a blinking block cursor when it scrolls into view.
 * Untyped letters stay in the layout (just invisible), so nothing jumps while it types.
 */
const TypedHeading: FC<TypedHeadingProps> = ({
    text,
    id,
    className,
    speed = 95
}): ReactNode => {
    const [ref, inView] = useInView({ threshold: 0.4 })
    const chars = useMemo(() => Array.from(text), [text])
    const [count, setCount] = useState(0)

    useEffect(() => {
        if (!inView) return

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            setCount(chars.length)
            return
        }

        let current = 0

        const timer = window.setInterval(() => {
            current += 1
            setCount(current)

            if (current >= chars.length) window.clearInterval(timer)
        }, speed)

        return () => window.clearInterval(timer)
    }, [inView, chars.length, speed])

    return (
        <h2 ref={ref} id={id} className={cn('break-words', className)}>
            <span className='sr-only'>{text}</span>

            <span aria-hidden='true'>
                {chars.slice(0, count).join('')}
                <span className='inline-block h-[0.78em] w-[0.4em] translate-y-[0.04em] animate-caret bg-bubblegum align-baseline' />
                <span className='opacity-0'>{chars.slice(count).join('')}</span>
            </span>
        </h2>
    )
}

export default TypedHeading
